
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { format } from 'date-fns';
import * as FactCheckService from '@/services/FactCheckService';
import { Factcheck, FactcheckType } from '@/types';

/** Factcheck picker for content fields that store factcheck ids: one id (a string) or several (a string array). */
@Component
export default class FactcheckField extends Vue {
  @Prop({ required: true }) readonly value!: string | string[];
  @Prop({ required: true }) readonly label!: string;
  @Prop({ type: Boolean, default: false }) readonly multiple!: boolean;
  @Prop({ default: '' }) readonly hint!: string;
  @Prop({ type: Array, default: () => [] }) readonly errorMessages!: string[];

  search: string | null = null;
  results: Factcheck[] = [];
  /** The chosen factchecks, kept so they still show by title when they drop out of the results. */
  selected: Factcheck[] = [];
  loading = false;
  private searchTimer: number | undefined;
  private requestCount = 0;

  get selectedIds(): string[] {
    if (Array.isArray(this.value)) return this.value;
    return this.value ? [this.value] : [];
  }

  get items(): Factcheck[] {
    const ids = new Set(this.results.map((factcheck) => factcheck.id));
    return [...this.selected.filter((factcheck) => !ids.has(factcheck.id)), ...this.results];
  }

  async mounted(): Promise<void> {
    await Promise.all([this.loadSelected(), this.runSearch('')]);
  }

  @Watch('search')
  onSearch(query: string | null): void {
    window.clearTimeout(this.searchTimer);
    // Typing into a single select shows the chosen title as the search text; that is not a new search.
    if (!this.multiple && query && this.selected.some((factcheck) => factcheck.title === query)) return;
    this.searchTimer = window.setTimeout(() => this.runSearch(query || ''), 300);
  }

  async loadSelected(): Promise<void> {
    const known = new Set(this.selected.map((factcheck) => factcheck.id));
    const missing = this.selectedIds.filter((id) => !known.has(id));
    if (missing.length === 0) return;
    const found = await Promise.all(missing.map((id) => FactCheckService.GetFactCheck(id).catch(() => null)));
    // An id that no longer exists still shows, so the editor can see and remove it.
    const loaded = missing.map((id, index) => found[index] || this.placeholder(id));
    this.selected = [...this.selected, ...loaded];
  }

  async runSearch(query: string): Promise<void> {
    const request = ++this.requestCount;
    this.loading = true;
    try {
      const results = await FactCheckService.GetAllFactChecks({ query, dateRange: null, numericRating: null, category: null, partners: null }, 20, 0);
      if (request === this.requestCount) this.results = results;
    } catch (error) {
      if (request === this.requestCount) this.results = [];
    }
    if (request === this.requestCount) this.loading = false;
  }

  onChange(next: string | string[] | null | undefined): void {
    const ids = Array.isArray(next) ? next : next ? [next] : [];
    const byId = new Map([...this.selected, ...this.results].map((factcheck) => [factcheck.id, factcheck]));
    this.selected = ids.map((id) => byId.get(id) || this.placeholder(id));
    this.$emit('input', this.multiple ? ids : ids[0] || '');
    if (this.multiple) this.search = null;
  }

  subtitle(factcheck: Factcheck): string {
    const partner = factcheck.Claim?.ClaimReviews?.[0]?.Partner?.name;
    const date = factcheck.sortDate ? format(new Date(factcheck.sortDate), 'dd/MM/yyyy') : '';
    return [date, partner].filter(Boolean).join(' · ');
  }

  private placeholder(id: string): Factcheck {
    return {
      id, title: `Onbekende factcheck (${id})`, isPublished: false, description: '', Claim: null, type: FactcheckType.FACTCHECK,
      UserId: null, ClaimId: null, sortDate: '', claimReviewPublished: false, childFriendly: false, deletedAt: null,
    };
  }
}
